import React from 'react';
import { Printer, X, Scale, FileText } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { TabularNumber } from '@/components/common/TabularNumber';
import { PigSaleRecord } from '@/types/database';

interface ScaleSlipPrintModalProps {
  isOpen: boolean;
  onClose: () => void;
  sale: PigSaleRecord | null;
}

export const ScaleSlipPrintModal: React.FC<ScaleSlipPrintModalProps> = ({
  isOpen,
  onClose,
  sale,
}) => {
  if (!sale) return null;

  const handlePrint = () => {
    window.print();
  };

  const items = sale.pig_sale_items || [];
  const headCount = Number(sale.head_count) || items.length;
  const grossKg = Number(sale.gross_weight_kg) || 0;
  const shrinkRate = Number(sale.shrinkage_rate_applied) || 0;
  const shrinkKg = Number(sale.shrinkage_deduction_kg) || headCount * shrinkRate;
  const netKg = Number(sale.net_weight_kg) || Math.max(0, grossKg - shrinkKg);
  const priceKg = Number(sale.price_per_kg) || 0;
  const totalAmount = Number(sale.total_amount) || netKg * priceKg;
  const avgNet = headCount > 0 ? netKg / headCount : 0;
  const avgGross = headCount > 0 ? grossKg / headCount : 0;

  // Split items into 2 or 3 columns for printing
  const numColumns = items.length > 30 ? 3 : items.length > 10 ? 2 : 1;
  const itemsPerCol = Math.ceil(items.length / numColumns);
  const columns: typeof items[] = [];
  for (let c = 0; c < numColumns; c++) {
    columns.push(items.slice(c * itemsPerCol, (c + 1) * itemsPerCol));
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] flex flex-col p-0 overflow-hidden bg-slate-100 print:bg-white print:max-w-none print:m-0 print:p-0 print:border-none print:shadow-none">
        {/* Modal Action Bar (Hidden on print) */}
        <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-slate-200 print:hidden">
          <div className="flex items-center gap-2">
            <Scale className="w-5 h-5 text-emerald-700" />
            <div>
              <h3 className="text-sm font-bold text-slate-900">Operational Weighing Scale Slip</h3>
              <p className="text-xs text-slate-500">
                Ticket: {sale.scale_ticket_no || sale.sale_number || 'N/A'} • {sale.sale_date}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={handlePrint}
              className="bg-emerald-700 hover:bg-emerald-600 font-semibold gap-1.5 text-white"
            >
              <Printer className="w-4 h-4" /> Print Scale Slip
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose} className="text-slate-500">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Printable Document Sheet */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 print:p-0 print:overflow-visible no-scrollbar">
          <div className="max-w-3xl mx-auto bg-white p-6 sm:p-8 rounded-lg shadow-sm border border-slate-200 print:border-none print:shadow-none print:p-4 text-slate-900 printable-slip">
            
            {/* Header */}
            <div className="border-b-2 border-slate-900 pb-3 mb-4 flex justify-between items-start">
              <div>
                <h1 className="text-xl font-black uppercase tracking-tight text-slate-900">
                  VSB Farm Contract Growing
                </h1>
                <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                  Live Hog Scale Weighing & Lift Summary
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Farm: <span className="font-semibold text-slate-800">{sale.production_cycles?.farms?.farm_name || 'VSB Farm'}</span>
                  {sale.production_cycles?.farms?.barangay && ` • ${sale.production_cycles?.farms?.barangay}, ${sale.production_cycles?.farms?.municipality_city || ''}`}
                </p>
              </div>
              <div className="text-right">
                <div className="inline-block px-2.5 py-1 bg-slate-100 rounded border border-slate-300 font-mono text-xs font-bold text-slate-800">
                  Ticket #{sale.scale_ticket_no || sale.sale_number || 'N/A'}
                </div>
                <p className="text-[11px] text-slate-500 mt-1 font-mono">
                  Date: <span className="font-semibold text-slate-800">{sale.sale_date}</span>
                </p>
                {sale.gate_pass_no && (
                  <p className="text-[10px] text-slate-500 font-mono">
                    Gate Pass: <span className="font-semibold text-slate-800">{sale.gate_pass_no}</span>
                  </p>
                )}
              </div>
            </div>

            {/* Metadata Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs mb-4">
              <div>
                <span className="text-[10px] uppercase font-bold text-slate-500 block">Production Cycle</span>
                <span className="font-bold font-mono text-slate-900">{sale.production_cycles?.cycle_code || 'N/A'}</span>
              </div>
              <div>
                <span className="text-[10px] uppercase font-bold text-slate-500 block">Grower / Raiser</span>
                <span className="font-semibold text-slate-800">{sale.production_cycles?.growers?.full_name || 'N/A'}</span>
              </div>
              <div>
                <span className="text-[10px] uppercase font-bold text-slate-500 block">Buyer / Customer</span>
                <span className="font-bold text-slate-900">{sale.buyers?.buyer_name || 'N/A'}</span>
              </div>
              <div>
                <span className="text-[10px] uppercase font-bold text-slate-500 block">Vehicle / Driver</span>
                <span className="text-slate-800 font-medium font-mono">
                  {sale.vehicle_plate_no ? `${sale.vehicle_plate_no}` : '—'}
                </span>
              </div>
            </div>

            {/* Pricing / Shrinkage Parameters */}
            <div className="flex flex-wrap items-center justify-between gap-2 p-2.5 rounded bg-emerald-50/70 border border-emerald-200 text-xs text-emerald-950 mb-4 font-mono">
              <div>
                <span className="font-bold">Base Price: </span>
                <span>₱{priceKg.toFixed(2)} / kg</span>
              </div>
              <div>
                <span className="font-bold">Applied Shrinkage: </span>
                <span>{shrinkRate.toFixed(2)} kg/head</span>
                {sale.rate_rules?.rule_name && (
                  <span className="text-[10px] text-emerald-800 ml-1">({sale.rate_rules.rule_name})</span>
                )}
              </div>
              <div>
                <span className="font-bold">Account: </span>
                <span>{sale.transaction_categories?.code || '1010'} - {sale.transaction_categories?.name || 'Pig Sales'}</span>
              </div>
            </div>

            {/* Granular Scale Lines (Multi-Column Layout) */}
            {items.length > 0 ? (
              <div className="mb-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                  Individual Scale Chute Weight Lines ({items.length} records)
                </div>
                <div className={`grid grid-cols-${numColumns} gap-2 text-[11px]`}>
                  {columns.map((col, cIdx) => (
                    <div key={cIdx} className="border border-slate-200 rounded overflow-hidden">
                      <table className="w-full text-left border-collapse">
                        <thead className="bg-slate-100 text-slate-700 font-semibold border-b border-slate-200 text-[10px]">
                          <tr>
                            <th className="py-1 px-1.5 w-7 text-center">#</th>
                            <th className="py-1 px-1 w-7 text-center">Qty</th>
                            <th className="py-1 px-1.5 text-right">Gross</th>
                            <th className="py-1 px-1 text-right text-amber-800">Shrink</th>
                            <th className="py-1 px-1.5 text-right text-emerald-800">Net kg</th>
                            <th className="py-1 px-1.5 text-right">Price</th>
                            <th className="py-1 px-1.5 text-right">Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 font-mono">
                          {col.map((it) => {
                            const itQty = Number(it.head_count) || 1;
                            const itGross = Number(it.gross_weight_kg) || 0;
                            const itShrink = Number(it.shrinkage_kg || shrinkRate * itQty);
                            const itNet = Number(it.net_weight_kg || Math.max(0, itGross - itShrink));
                            const itPrice = Number(it.price_per_kg || priceKg);
                            const itTotal = Number(it.total_amount || (itNet * itPrice));

                            return (
                              <tr key={it.sequence_no} className="hover:bg-slate-50">
                                <td className="py-0.5 px-1.5 text-center text-slate-500">{it.sequence_no}</td>
                                <td className="py-0.5 px-1 text-center font-bold text-slate-800">{itQty}</td>
                                <td className="py-0.5 px-1.5 text-right font-medium">
                                  <TabularNumber value={itGross} decimals={1} />
                                </td>
                                <td className="py-0.5 px-1 text-right text-amber-700">
                                  <TabularNumber value={itShrink} decimals={1} />
                                </td>
                                <td className="py-0.5 px-1.5 text-right font-bold text-emerald-800">
                                  <TabularNumber value={itNet} decimals={1} />
                                </td>
                                <td className="py-0.5 px-1.5 text-right text-slate-600">
                                  ₱{itPrice.toFixed(0)}
                                </td>
                                <td className="py-0.5 px-1.5 text-right font-bold text-slate-900">
                                  <TabularNumber value={itTotal} type="currency" decimals={2} />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="p-4 text-center border border-slate-200 rounded text-xs text-slate-500 mb-4 font-mono">
                Grouped scale record: {headCount} head @ {grossKg.toFixed(2)} gross kg
              </div>
            )}

            {/* Sale Totals Summary Box */}
            <div className="p-3.5 rounded-lg bg-slate-900 text-white mb-6 text-xs">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pb-3 border-b border-slate-800">
                <div>
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider block">Total Pigs Sold</span>
                  <span className="text-sm font-bold font-mono text-emerald-400">{headCount} head</span>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider block">Total Gross Weight</span>
                  <span className="text-sm font-bold font-mono text-slate-100">
                    <TabularNumber value={grossKg} decimals={2} /> kg
                  </span>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider block">Shrinkage Deduction</span>
                  <span className="text-sm font-bold font-mono text-amber-300">
                    -<TabularNumber value={shrinkKg} decimals={2} /> kg
                  </span>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider block">Total Net Sale Weight</span>
                  <span className="text-sm font-bold font-mono text-emerald-300">
                    <TabularNumber value={netKg} decimals={2} /> kg
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 pt-3">
                <div className="flex gap-4 text-[11px] text-slate-300 font-mono">
                  <span>Avg Gross: {avgGross.toFixed(2)} kg/hd</span>
                  <span>•</span>
                  <span>Avg Net: {avgNet.toFixed(2)} kg/hd</span>
                </div>
                <div className="text-right">
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider block">Total Sale Amount</span>
                  <span className="text-lg font-black font-mono text-emerald-400">
                    <TabularNumber value={totalAmount} type="currency" decimals={2} />
                  </span>
                </div>
              </div>
            </div>

            {/* Operational Signatures Section */}
            <div className="grid grid-cols-3 gap-6 pt-4 border-t border-slate-300 text-center text-xs mt-6">
              <div>
                <div className="h-10 border-b border-slate-400"></div>
                <p className="font-semibold text-slate-800 mt-1.5">Weighed By</p>
                <p className="text-[10px] text-slate-500">Scale Chute Operator</p>
              </div>
              <div>
                <div className="h-10 border-b border-slate-400"></div>
                <p className="font-semibold text-slate-800 mt-1.5">Verified By</p>
                <p className="text-[10px] text-slate-500">Grower / Farm Representative</p>
              </div>
              <div>
                <div className="h-10 border-b border-slate-400"></div>
                <p className="font-semibold text-slate-800 mt-1.5">Received By</p>
                <p className="text-[10px] text-slate-500">Buyer / Driver Representative</p>
              </div>
            </div>

            {/* Notes / Footer */}
            {sale.notes && (
              <div className="mt-4 pt-2 border-t border-slate-100 text-[11px] text-slate-500 italic">
                Notes: {sale.notes}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
